/**
 * Holds the editor back until sign-in has finished.
 *
 * Rendering the editor first and signing in "in the background" is how the
 * app ended up sending API calls with no token at all: every request failed
 * with "Missing bearer token". Nothing below this component runs until there
 * is an account that can get one -- or the server says auth is off.
 */

import { useEffect, useState, type ReactNode } from 'react'

import { deliverLaunch } from '../files/fileAccess'
import { takeCarry, type OpenDocument } from './carry'
import { initAuth, onSignInExpired, redirectToSignIn } from './msal'

type Phase =
  | { kind: 'starting' }
  | { kind: 'redirecting' }
  | { kind: 'ready'; resume: OpenDocument | null }
  | { kind: 'error'; message: string }

export function AuthGate({
  children,
}: {
  children: (resume: OpenDocument | null) => ReactNode
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'starting' })
  const [expired, setExpired] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [expiredError, setExpiredError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    initAuth()
      .then(async (outcome) => {
        if (!alive) return
        if (outcome.kind === 'redirecting') {
          setPhase({ kind: 'redirecting' })
          return
        }
        const carried = await takeCarry()
        carried?.launches.forEach(deliverLaunch)
        if (alive) setPhase({ kind: 'ready', resume: carried?.open ?? null })
      })
      .catch((err: unknown) => {
        if (alive) setPhase({ kind: 'error', message: messageOf(err) })
      })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => onSignInExpired(() => setExpired(true)), [])

  if (phase.kind === 'starting' || phase.kind === 'redirecting') {
    return (
      <div className="auth-screen" role="status" aria-live="polite">
        <img className="auth-mark" src="/app-icons/odie-192.png" alt="" width={96} height={96} />
        <p>
          {phase.kind === 'starting'
            ? 'Signing you in…'
            : 'Taking you to Microsoft sign-in…'}
        </p>
      </div>
    )
  }

  if (phase.kind === 'error') {
    return (
      <div className="auth-screen" role="alert">
        <img className="auth-mark" src="/app-icons/odie-192.png" alt="" width={96} height={96} />
        <h1>Could not sign you in</h1>
        <p className="auth-detail">{phase.message}</p>
        <button type="button" className="auth-button" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    )
  }

  const signInAgain = async () => {
    setLeaving(true)
    setExpiredError(null)
    try {
      await redirectToSignIn()
    } catch (err) {
      setLeaving(false)
      setExpiredError(messageOf(err))
    }
  }

  return (
    <>
      {children(phase.resume)}
      {expired && (
        <div className="auth-overlay" role="dialog" aria-modal="true" aria-labelledby="auth-expired-title">
          <div className="auth-card">
            <h2 id="auth-expired-title">Your sign-in has expired</h2>
            <p>
              Your document stays open on the server, and you will come back
              to it after signing in. The change you just made did not go
              through, so you may need to repeat it.
            </p>
            {expiredError && <p className="auth-detail">{expiredError}</p>}
            <button type="button" className="auth-button" onClick={signInAgain} disabled={leaving}>
              {leaving ? 'Taking you to sign-in…' : 'Sign in again'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}
